import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { KpiGroup, KpiTrack, StrategyKpiDeep, StrategyOverview } from '@mnc/contracts';
import { useAuth } from '../../lib/auth';
import { pick } from '../../i18n';
import { Badge, cn } from '../../components/ui';
import { TrackerModal } from './Kpis';
import { LEVEL_TONE, ProgressRing, trackTone, useLevelLabel } from './shared';

const GROUPS: KpiGroup[] = ['input', 'output', 'result'];
const TRACKS: KpiTrack[] = ['achieved', 'on_track', 'behind', 'no_target'];
const GROUP_DOT: Record<KpiGroup, string> = { input: 'bg-blue-500', output: 'bg-purple-500', result: 'bg-teal-500' };
const fmt = (n: number | null, locale: string) => (n === null ? '—' : new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(n));

/** Every KPI in the scope, grouped by node then input / output / result (MRLCO KPI dashboard), with tracking filters. */
export function KpiDashboard({ data }: { data: StrategyOverview }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { can, canAction } = useAuth();
  const levelLabel = useLevelLabel(data.config);
  const [group, setGroup] = useState<KpiGroup | ''>('');
  const [track, setTrack] = useState<KpiTrack | ''>('');
  const [tracking, setTracking] = useState<StrategyKpiDeep | null>(null);
  const kpis = useMemo(() => data.kpis.filter((k) => (!group || k.kpi_group === group) && (!track || k.track === track)), [data.kpis, group, track]);
  const byNode = useMemo(() => {
    const map = new Map<string, { code: string; name: string; level: StrategyKpiDeep['node_level']; items: StrategyKpiDeep[] }>();
    for (const k of kpis) {
      if (!map.has(k.node_id)) map.set(k.node_id, { code: k.node_code, name: pick(i18n.language, k.node_name_en, k.node_name_ar), level: k.node_level, items: [] });
      map.get(k.node_id)!.items.push(k);
    }
    return [...map.entries()];
  }, [kpis, i18n.language]);
  const s = data.summary.kpis;

  return (
    <div className="grid gap-4">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        <button type="button" onClick={() => setTrack('')} className={cn('card p-3 text-start', track === '' && 'ring-2 ring-brand-500')}><div className="text-[10px] font-semibold uppercase text-neutral-500">{t('strategy.kpiDashboard.total')}</div><div className="text-xl font-semibold">{s.total}</div></button>
        {TRACKS.map((tr) => (
          <button key={tr} type="button" onClick={() => setTrack(track === tr ? '' : tr)} className={cn('card p-3 text-start', track === tr && 'ring-2 ring-brand-500')}>
            <div className="text-[10px] font-semibold uppercase text-neutral-500">{t(`strategy.kpiDashboard.track.${tr}`)}</div>
            <div className="flex items-center gap-2 text-xl font-semibold"><Badge tone={trackTone(tr)}>{s[tr]}</Badge></div>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <button type="button" onClick={() => setGroup('')} className={cn('rounded-full border px-2.5 py-1', group === '' ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-700/30 dark:text-brand-100' : 'border-neutral-300 dark:border-neutral-700')}>{t('strategy.kpiDashboard.allGroups')}</button>
        {GROUPS.map((g) => (
          <button key={g} type="button" onClick={() => setGroup(group === g ? '' : g)} className={cn('inline-flex items-center gap-1 rounded-full border px-2.5 py-1', group === g ? 'border-brand-600 bg-brand-50 text-brand-700 dark:bg-brand-700/30 dark:text-brand-100' : 'border-neutral-300 dark:border-neutral-700')}><span className={cn('h-2 w-2 rounded-full', GROUP_DOT[g])} />{t(`strategy.kpi.groups.${g}`)}</button>
        ))}
      </div>
      {byNode.length === 0 && <div className="card p-8 text-center text-sm text-neutral-500">{t('strategy.kpiDashboard.none')}</div>}
      {byNode.map(([nodeId, n]) => (
        <div key={nodeId} className="card">
          <button type="button" className="flex w-full flex-wrap items-center gap-2 border-b border-neutral-200 px-4 py-2 text-start dark:border-neutral-800" onClick={() => void navigate({ to: '/strategy/nodes/$id', params: { id: nodeId } })}>
            <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase', LEVEL_TONE[n.level])}>{levelLabel(n.level)}</span>
            <code className="text-xs text-neutral-500">{n.code}</code>
            <span className="font-semibold" dir="auto">{n.name}</span>
            <span className="text-xs text-neutral-500">({n.items.length})</span>
          </button>
          <div className="grid gap-3 p-3">
            {GROUPS.filter((g) => n.items.some((k) => k.kpi_group === g)).map((g) => (
              <div key={g}>
                <div className="mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase text-neutral-500"><span className={cn('h-2 w-2 rounded-full', GROUP_DOT[g])} />{t(`strategy.kpi.groups.${g}`)}</div>
                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                  {n.items.filter((k) => k.kpi_group === g).map((k) => {
                    const delta = k.current_value - k.previous_value;
                    return (
                      <button key={k.id} type="button" onClick={() => setTracking(k)} className="flex items-center gap-3 rounded-lg border border-neutral-200 p-2.5 text-start hover:border-brand-500 dark:border-neutral-800">
                        <ProgressRing value={k.progress} size={52} stroke={6} label={k.progress === null ? fmt(k.current_value, i18n.language) : undefined} />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium" dir="auto" title={pick(i18n.language, k.title_en, k.title_ar)}>{pick(i18n.language, k.title_en, k.title_ar)}</div>
                          <div className="text-xs text-neutral-500 tabular-nums">{fmt(k.current_value, i18n.language)}{k.target_value !== null && ` / ${fmt(k.target_value, i18n.language)}`} {pick(i18n.language, k.unit_name_en, k.unit_name_ar)}</div>
                          <div className="mt-1 flex flex-wrap items-center gap-1">
                            <Badge tone={trackTone(k.track)}>{t(`strategy.kpiDashboard.track.${k.track}`)}</Badge>
                            {k.log_count > 0 && delta !== 0 && <Badge tone={(delta > 0) !== k.lower_is_better ? 'brand' : 'danger'}>{delta > 0 ? '+' : ''}{fmt(delta, i18n.language)}</Badge>}
                            {k.expected_progress !== null && k.track !== 'achieved' && <span className="text-[10px] text-neutral-400">{t('strategy.kpiDashboard.expected', { value: Math.round(k.expected_progress) })}</span>}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
      {tracking && <TrackerModal kpi={tracking} canLog={can('strategy', 'can_edit')} manage={canAction('strategy.manage_kpis')} onClose={() => setTracking(null)} />}
    </div>
  );
}
